import React, { useState } from 'react';
import hoistStatics from 'hoist-non-react-statics';

const ModalService = React.createContext({
    modals: {},
    showModal: (modal, options) => {
    },
    hideModal: () => {
    },
});

ModalService.Consumer.displayName = 'ModalService';

export const useModalService = () => {
    const context = React.useContext(ModalService);
    if (context === undefined) {
        throw new Error('useModalService must be used within a ModalProvider');
    }
    return context;
};

export const ModalProvider = ({ children }) => {
    const [modals, setModals] = useState(null);

    const showModal = (modal, options) => {
        setModals(modal ? {
            current: modal,
            options: (options == null) ? {} : options
        } : null);
    };

    const hideModal = () => {
        setModals(null);
    };

    const context = {
        modals,
        showModal,
        hideModal
    };

    return (<ModalService.Provider value={context}>
        {children}
    </ModalService.Provider>);
};

ModalProvider.displayName = 'ModalProvider';

export const withModal = (Component) => {
    const displayName = 'withModal(' + (Component.displayName || Component.name) + ')';

    const Wrapper = ({ wrappedComponentRef, ...rest }) =>
        React.createElement(ModalService.Consumer, null, context => {
            const {
                modals,
                showModal,
                hideModal
            } = context;
            return (
                React.createElement(Component, Object.assign({}, {
                        modals,
                        showModal,
                        hideModal
                },
                    rest
                , {
                    ref: wrappedComponentRef
                })
                ));
        });

    Wrapper.displayName = displayName;
    Wrapper.WrappedComponent = Component;

    return hoistStatics(Wrapper, Component);
};
